import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useLang } from '../i18n'
import { useAuth } from '../auth'
import { TimeRange, TimeRangeValue, defaultTimeRange } from '../components/TimeRange'

interface Task {
  id: string
  category_code: string
  name: string
  counting_rule: string
  report_alias: string | null
  include_in_report: boolean
  region_scope: 'both' | 'cn_only' | 'us_only'
  sort_order: number
  is_active: boolean
  deleted_at: string | null
}

interface Category {
  id: string
  code: string
  display_name_en: string
  display_name_cn: string
  report_group: string | null
  sort_order: number
  tasks: Task[]
}

interface AddFormState {
  name: string
  counting_rule: string
  report_alias: string
  include_in_report: boolean
  region_scope: 'both' | 'cn_only' | 'us_only'
  error: string | null
  submitting: boolean
}

interface EditState {
  name: string
  counting_rule: string
  report_alias: string
  include_in_report: boolean
  region_scope: 'both' | 'cn_only' | 'us_only'
  error: string | null
  submitting: boolean
}

interface HardDeleteState {
  task: Task
  refCount: number | null
  inputValue: string
  submitting: boolean
  error: string | null
}

interface NoncompliantRecord {
  id: string
  work_date: string
  member_display_name: string
  region_code: string
  group_name: string
  category_display_name_en: string
  task_name: string
  volume: number
  hours: string
  note: string
}

interface NoncompliantSectionData {
  data: NoncompliantRecord[]
  page: { page: number; page_size: number; total: number }
}

const inputStyle: React.CSSProperties = {
  padding: '5px 8px',
  border: '1px solid var(--border)',
  borderRadius: '4px',
  fontSize: '13px',
  width: '100%',
  boxSizing: 'border-box',
  background: 'var(--bg-surface)',
  color: 'var(--text-primary)',
}

const btnBase: React.CSSProperties = {
  padding: '4px 10px',
  border: '1px solid var(--border)',
  borderRadius: '4px',
  fontSize: '12px',
  cursor: 'pointer',
  background: 'var(--bg-surface)',
  color: 'var(--text-primary)',
}

const btnPrimary: React.CSSProperties = {
  ...btnBase,
  background: 'var(--accent)',
  borderColor: 'var(--accent)',
  color: 'var(--accent-contrast)',
}

const btnDanger: React.CSSProperties = {
  ...btnBase,
  background: 'var(--danger)',
  borderColor: 'var(--danger)',
  color: 'var(--accent-contrast)',
}

function RegionScopeSelect({
  value,
  onChange,
  t,
}: {
  value: 'both' | 'cn_only' | 'us_only'
  onChange: (v: 'both' | 'cn_only' | 'us_only') => void
  t: (k: string) => string
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as 'both' | 'cn_only' | 'us_only')}
      style={{ ...inputStyle, width: 'auto' }}
    >
      <option value="both">{t('taskDictRegionBoth')}</option>
      <option value="cn_only">{t('taskDictRegionCnOnly')}</option>
      <option value="us_only">{t('taskDictRegionUsOnly')}</option>
    </select>
  )
}

function regionLabel(scope: string, t: (k: string) => string): string {
  if (scope === 'cn_only') return t('taskDictRegionCnOnly')
  if (scope === 'us_only') return t('taskDictRegionUsOnly')
  return t('taskDictRegionBoth')
}

export function TaskDictPage() {
  const { t } = useLang()
  const { accessToken, user } = useAuth()
  const location = useLocation()

  const canEdit = user?.role === 'manager' || user?.role === 'admin'

  const [categories, setCategories] = useState<Category[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showInactive, setShowInactive] = useState(false)

  // editingTaskId -> EditState
  const [editMap, setEditMap] = useState<Record<string, EditState>>({})

  // addForms: categoryCode -> AddFormState
  const [addForms, setAddForms] = useState<Record<string, AddFormState>>({})

  // Hard delete dialog state
  const [hardDelete, setHardDelete] = useState<HardDeleteState | null>(null)

  // Drag state
  const dragTaskId = useRef<string | null>(null)
  const dragCategoryCode = useRef<string | null>(null)

  // Noncompliant section state
  const noncompliantSectionRef = useRef<HTMLDivElement | null>(null)
  const [ncTimeRange, setNcTimeRange] = useState<TimeRangeValue>(defaultTimeRange())
  const [ncRegion, setNcRegion] = useState<string>('')
  const [ncPage, setNcPage] = useState(1)
  const [ncData, setNcData] = useState<NoncompliantSectionData | null>(null)
  const [ncLoading, setNcLoading] = useState(false)
  const [ncError, setNcError] = useState<string | null>(null)

  // Scroll to noncompliant section when navigated via hash
  useEffect(() => {
    if (location.hash === '#noncompliant-section' && noncompliantSectionRef.current) {
      noncompliantSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [location.hash])

  // Fetch noncompliant data
  useEffect(() => {
    if (!accessToken) return
    setNcLoading(true)
    setNcError(null)
    const params = new URLSearchParams({
      range: ncTimeRange.range,
      page: String(ncPage),
      page_size: '50',
    })
    if (ncTimeRange.range === 'custom') {
      if (ncTimeRange.from) params.set('from', ncTimeRange.from)
      if (ncTimeRange.to) params.set('to', ncTimeRange.to)
    }
    if (ncRegion) params.set('region', ncRegion)
    fetch(`/api/noncompliant?${params.toString()}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    })
      .then(async (res) => {
        if (!res.ok) {
          const body = await res.json().catch(() => ({}))
          throw new Error(body?.error?.message ?? `HTTP ${res.status}`)
        }
        return res.json() as Promise<NoncompliantSectionData>
      })
      .then((d) => { setNcData(d); setNcLoading(false) })
      .catch((err: Error) => { setNcError(err.message); setNcLoading(false) })
  }, [accessToken, ncTimeRange, ncRegion, ncPage])

  const fetchCategories = useCallback(async () => {
    if (!accessToken) return
    setLoading(true)
    setError(null)
    try {
      const params = showInactive ? '?include_inactive=true' : ''
      const resp = await fetch(`/api/categories${params}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}))
        setError(body?.error?.message ?? t('errorGeneric'))
        return
      }
      const data = await resp.json()
      setCategories(data.data ?? [])
    } catch {
      setError(t('errorGeneric'))
    } finally {
      setLoading(false)
    }
  }, [accessToken, showInactive, t])

  useEffect(() => {
    fetchCategories()
  }, [fetchCategories])

  // ---- Add task ----
  function initAddForm(categoryCode: string) {
    setAddForms((prev) => ({
      ...prev,
      [categoryCode]: {
        name: '',
        counting_rule: '',
        report_alias: '',
        include_in_report: false,
        region_scope: 'both',
        error: null,
        submitting: false,
      },
    }))
  }

  function updateAddForm(categoryCode: string, patch: Partial<AddFormState>) {
    setAddForms((prev) => ({
      ...prev,
      [categoryCode]: { ...prev[categoryCode], ...patch },
    }))
  }

  async function submitAddTask(categoryCode: string) {
    const form = addForms[categoryCode]
    if (!form || !accessToken) return
    if (!form.name.trim()) {
      updateAddForm(categoryCode, { error: t('taskDictTaskName') + ' required' })
      return
    }
    if (!form.counting_rule.trim()) {
      updateAddForm(categoryCode, { error: t('taskDictCountingRule') + ' required' })
      return
    }
    updateAddForm(categoryCode, { submitting: true, error: null })
    try {
      const resp = await fetch('/api/tasks', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          category_code: categoryCode,
          name: form.name.trim(),
          counting_rule: form.counting_rule.trim(),
          report_alias: form.report_alias.trim() || null,
          include_in_report: form.include_in_report,
          region_scope: form.region_scope,
        }),
      })
      if (resp.status === 409) {
        updateAddForm(categoryCode, { submitting: false, error: t('taskDictDuplicateName') })
        return
      }
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}))
        updateAddForm(categoryCode, { submitting: false, error: body?.error?.message ?? t('errorGeneric') })
        return
      }
      // Reset form and refresh
      setAddForms((prev) => {
        const next = { ...prev }
        delete next[categoryCode]
        return next
      })
      await fetchCategories()
    } catch {
      updateAddForm(categoryCode, { submitting: false, error: t('errorGeneric') })
    }
  }

  // ---- Edit task ----
  function startEdit(task: Task) {
    setEditMap((prev) => ({
      ...prev,
      [task.id]: {
        name: task.name,
        counting_rule: task.counting_rule,
        report_alias: task.report_alias ?? '',
        include_in_report: task.include_in_report,
        region_scope: task.region_scope,
        error: null,
        submitting: false,
      },
    }))
  }

  function cancelEdit(taskId: string) {
    setEditMap((prev) => {
      const next = { ...prev }
      delete next[taskId]
      return next
    })
  }

  function updateEdit(taskId: string, patch: Partial<EditState>) {
    setEditMap((prev) => ({
      ...prev,
      [taskId]: { ...prev[taskId], ...patch },
    }))
  }

  async function submitEdit(task: Task) {
    const form = editMap[task.id]
    if (!form || !accessToken) return
    if (!form.name.trim()) {
      updateEdit(task.id, { error: t('taskDictTaskName') + ' required' })
      return
    }
    if (!form.counting_rule.trim()) {
      updateEdit(task.id, { error: t('taskDictCountingRule') + ' required' })
      return
    }
    updateEdit(task.id, { submitting: true, error: null })
    try {
      const resp = await fetch(`/api/tasks/${task.id}`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: form.name.trim(),
          counting_rule: form.counting_rule.trim(),
          report_alias: form.report_alias.trim() || null,
          include_in_report: form.include_in_report,
          region_scope: form.region_scope,
        }),
      })
      if (resp.status === 409) {
        updateEdit(task.id, { submitting: false, error: t('taskDictDuplicateName') })
        return
      }
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}))
        updateEdit(task.id, { submitting: false, error: body?.error?.message ?? t('errorGeneric') })
        return
      }
      cancelEdit(task.id)
      await fetchCategories()
    } catch {
      updateEdit(task.id, { submitting: false, error: t('errorGeneric') })
    }
  }

  // ---- Soft delete ----
  async function softDeleteTask(taskId: string) {
    if (!accessToken) return
    try {
      const resp = await fetch(`/api/tasks/${taskId}?hard=false`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}))
        setError(body?.error?.message ?? t('errorGeneric'))
        return
      }
      await fetchCategories()
    } catch {
      setError(t('errorGeneric'))
    }
  }

  // ---- Hard delete ----
  async function openHardDelete(task: Task) {
    if (!accessToken) return
    setHardDelete({ task, refCount: null, inputValue: '', submitting: false, error: null })
    try {
      const resp = await fetch(`/api/tasks/${task.id}/ref-count`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      if (resp.ok) {
        const data = await resp.json()
        setHardDelete((prev) => prev ? { ...prev, refCount: data.ref_count ?? 0 } : null)
      } else {
        setHardDelete((prev) => prev ? { ...prev, refCount: 0 } : null)
      }
    } catch {
      setHardDelete((prev) => prev ? { ...prev, refCount: 0 } : null)
    }
  }

  async function confirmHardDelete() {
    if (!hardDelete || !accessToken) return
    setHardDelete((prev) => prev ? { ...prev, submitting: true, error: null } : null)
    try {
      const resp = await fetch(`/api/tasks/${hardDelete.task.id}?hard=true`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
      })
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}))
        setHardDelete((prev) => prev ? { ...prev, submitting: false, error: body?.error?.message ?? t('errorGeneric') } : null)
        return
      }
      setHardDelete(null)
      await fetchCategories()
    } catch {
      setHardDelete((prev) => prev ? { ...prev, submitting: false, error: t('errorGeneric') } : null)
    }
  }

  // ---- Drag and drop ----
  function handleDragStart(taskId: string, categoryCode: string) {
    dragTaskId.current = taskId
    dragCategoryCode.current = categoryCode
  }

  function handleDragOver(e: React.DragEvent) {
    e.preventDefault()
  }

  async function handleDrop(e: React.DragEvent, targetTaskId: string, targetCategoryCode: string) {
    e.preventDefault()
    const srcId = dragTaskId.current
    const srcCatCode = dragCategoryCode.current
    if (!srcId || srcId === targetTaskId) return
    if (srcCatCode !== targetCategoryCode) return // cross-category not allowed
    if (!accessToken) return

    // Find the category and reorder locally
    const cat = categories.find((c) => c.code === targetCategoryCode)
    if (!cat) return

    const tasks = [...cat.tasks]
    const srcIdx = tasks.findIndex((t) => t.id === srcId)
    const tgtIdx = tasks.findIndex((t) => t.id === targetTaskId)
    if (srcIdx === -1 || tgtIdx === -1) return

    const [moved] = tasks.splice(srcIdx, 1)
    tasks.splice(tgtIdx, 0, moved)

    // Optimistic update
    setCategories((prev) =>
      prev.map((c) => (c.code === targetCategoryCode ? { ...c, tasks } : c))
    )

    // Call reorder API
    try {
      await fetch('/api/tasks/reorder', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ ordered_ids: tasks.map((t) => t.id) }),
      })
    } catch {
      // Revert on error
      await fetchCategories()
    }
  }

  // ---- Render helpers ----
  function renderTaskRow(task: Task, categoryCode: string) {
    const isEditing = !!editMap[task.id]
    const editState = editMap[task.id]
    const inactive = !task.is_active

    if (isEditing && editState) {
      const countingRuleChanged = editState.counting_rule !== task.counting_rule
      return (
        <tr key={task.id} style={{ background: 'var(--info-bg)' }}>
          {canEdit && (
            <td style={tdStyle}>
              <span style={{ color: 'var(--border-strong)', cursor: 'default' }}>⠿</span>
            </td>
          )}
          <td style={tdStyle}>
            <input
              style={inputStyle}
              value={editState.name}
              onChange={(e) => updateEdit(task.id, { name: e.target.value })}
            />
          </td>
          <td style={tdStyle}>
            <input
              style={inputStyle}
              value={editState.counting_rule}
              onChange={(e) => updateEdit(task.id, { counting_rule: e.target.value })}
            />
            {countingRuleChanged && (
              <div style={{ fontSize: '11px', color: 'var(--warning)', marginTop: '3px' }}>
                {t('taskDictCountingRuleHint')}
              </div>
            )}
          </td>
          <td style={tdStyle}>
            <input
              style={{ ...inputStyle }}
              value={editState.report_alias}
              onChange={(e) => updateEdit(task.id, { report_alias: e.target.value })}
              placeholder={t('taskDictReportAlias')}
            />
          </td>
          <td style={{ ...tdStyle, textAlign: 'center' }}>
            <input
              type="checkbox"
              checked={editState.include_in_report}
              onChange={(e) => updateEdit(task.id, { include_in_report: e.target.checked })}
            />
          </td>
          <td style={tdStyle}>
            <RegionScopeSelect value={editState.region_scope} onChange={(v) => updateEdit(task.id, { region_scope: v })} t={t} />
          </td>
          <td style={tdStyle}>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              <button
                style={btnPrimary}
                disabled={editState.submitting}
                onClick={() => submitEdit(task)}
              >
                {t('taskDictSave')}
              </button>
              <button
                style={btnBase}
                disabled={editState.submitting}
                onClick={() => cancelEdit(task.id)}
              >
                {t('taskDictCancel')}
              </button>
            </div>
            {editState.error && (
              <div style={{ color: 'var(--danger)', fontSize: '11px', marginTop: '4px' }}>{editState.error}</div>
            )}
          </td>
        </tr>
      )
    }

    return (
      <tr
        key={task.id}
        draggable={canEdit}
        onDragStart={() => handleDragStart(task.id, categoryCode)}
        onDragOver={handleDragOver}
        onDrop={(e) => handleDrop(e, task.id, categoryCode)}
        style={{
          opacity: inactive ? 0.55 : 1,
          background: inactive ? 'var(--bg-elevated)' : undefined,
          cursor: canEdit ? 'grab' : undefined,
        }}
      >
        {canEdit && (
          <td style={{ ...tdStyle, color: 'var(--border-strong)', cursor: 'grab', userSelect: 'none' }}>⠿</td>
        )}
        <td style={tdStyle}>
          {task.name}
          {inactive && (
            <span style={{
              marginLeft: '6px',
              padding: '1px 5px',
              background: 'var(--bg-elevated)',
              borderRadius: '3px',
              fontSize: '11px',
              color: 'var(--text-muted)',
            }}>
              {t('taskDictInactive')}
            </span>
          )}
        </td>
        <td style={tdStyle}>{task.counting_rule}</td>
        <td style={{ ...tdStyle, color: 'var(--text-secondary)' }}>{task.report_alias ?? '—'}</td>
        <td style={{ ...tdStyle, textAlign: 'center' }}>
          <input type="checkbox" checked={task.include_in_report} readOnly disabled style={{ cursor: 'default' }} />
        </td>
        <td style={tdStyle}>
          <span style={{
            padding: '2px 7px',
            borderRadius: '3px',
            fontSize: '11px',
            background: task.region_scope === 'cn_only' ? 'var(--success-bg)' : task.region_scope === 'us_only' ? 'var(--info-bg)' : 'var(--warning-bg)',
            color: task.region_scope === 'cn_only' ? 'var(--success)' : task.region_scope === 'us_only' ? 'var(--info)' : 'var(--warning)',
          }}>
            {regionLabel(task.region_scope, t)}
          </span>
        </td>
        {canEdit && (
          <td style={tdStyle}>
            <div style={{ display: 'flex', gap: '4px' }}>
              <button style={btnBase} onClick={() => startEdit(task)}>{t('taskDictEdit')}</button>
              {task.is_active && (
                <button style={{ ...btnBase, borderColor: 'var(--warning)', color: 'var(--warning)' }} onClick={() => softDeleteTask(task.id)}>
                  {t('taskDictSoftDelete')}
                </button>
              )}
              <button style={btnDanger} onClick={() => openHardDelete(task)}>
                {t('taskDictHardDelete')}
              </button>
            </div>
          </td>
        )}
      </tr>
    )
  }

  function renderAddForm(categoryCode: string) {
    const form = addForms[categoryCode]
    if (!form) {
      return (
        <tr>
          <td colSpan={canEdit ? 7 : 6} style={{ padding: '8px 10px', borderTop: '1px solid var(--border)' }}>
            <button style={btnPrimary} onClick={() => initAddForm(categoryCode)}>
              + {t('taskDictAddTask')}
            </button>
          </td>
        </tr>
      )
    }

    return (
      <tr style={{ background: 'var(--warning-bg)' }}>
        {canEdit && <td style={tdStyle} />}
        <td style={tdStyle}>
          <input
            style={inputStyle}
            placeholder={t('taskDictTaskName')}
            value={form.name}
            onChange={(e) => updateAddForm(categoryCode, { name: e.target.value })}
          />
        </td>
        <td style={tdStyle}>
          <input
            style={inputStyle}
            placeholder={t('taskDictCountingRule')}
            value={form.counting_rule}
            onChange={(e) => updateAddForm(categoryCode, { counting_rule: e.target.value })}
          />
        </td>
        <td style={tdStyle}>
          <input
            style={inputStyle}
            placeholder={t('taskDictReportAlias')}
            value={form.report_alias}
            onChange={(e) => updateAddForm(categoryCode, { report_alias: e.target.value })}
          />
        </td>
        <td style={{ ...tdStyle, textAlign: 'center' }}>
          <input
            type="checkbox"
            checked={form.include_in_report}
            onChange={(e) => updateAddForm(categoryCode, { include_in_report: e.target.checked })}
          />
        </td>
        <td style={tdStyle}>
          <RegionScopeSelect value={form.region_scope} onChange={(v) => updateAddForm(categoryCode, { region_scope: v })} t={t} />
        </td>
        <td style={tdStyle}>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            <button
              style={btnPrimary}
              disabled={form.submitting}
              onClick={() => submitAddTask(categoryCode)}
            >
              {t('taskDictSave')}
            </button>
            <button
              style={btnBase}
              disabled={form.submitting}
              onClick={() => {
                setAddForms((prev) => {
                  const next = { ...prev }
                  delete next[categoryCode]
                  return next
                })
              }}
            >
              {t('taskDictCancel')}
            </button>
          </div>
          {form.error && (
            <div style={{ color: 'var(--danger)', fontSize: '11px', marginTop: '4px' }}>{form.error}</div>
          )}
        </td>
      </tr>
    )
  }

  const tdStyle: React.CSSProperties = {
    padding: '8px 10px',
    borderBottom: '1px solid var(--border)',
    fontSize: '13px',
    verticalAlign: 'middle',
    color: 'var(--text-primary)',
  }

  const thStyle: React.CSSProperties = {
    ...tdStyle,
    background: 'var(--bg-page)',
    fontWeight: 600,
    fontSize: '12px',
    color: 'var(--text-secondary)',
    whiteSpace: 'nowrap',
  }

  return (
    <div style={{ maxWidth: '1300px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' }}>
        <h2 style={{ margin: 0 }}>{t('navTaskDict')}</h2>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: 'var(--text-secondary)', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          {t('taskDictShowInactive')}
        </label>
      </div>

      {error && (
        <div style={{ color: 'var(--danger)', marginBottom: '12px', padding: '8px 12px', background: 'var(--danger-bg)', borderRadius: '4px' }}>
          {error}
        </div>
      )}

      {loading ? (
        <p style={{ color: 'var(--text-muted)' }}>{t('loading')}</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {categories.map((category) => (
            <div
              key={category.code}
              style={{
                border: '1px solid var(--border)',
                borderRadius: '8px',
                overflow: 'hidden',
                boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
              }}
            >
              {/* Card header */}
              <div style={{
                background: 'var(--bg-page)',
                padding: '12px 16px',
                borderBottom: '1px solid var(--border)',
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
              }}>
                <span style={{ fontWeight: 700, fontSize: '15px' }}>{category.display_name_en}</span>
                <span style={{ color: 'var(--text-muted)', fontSize: '13px' }}>{category.display_name_cn}</span>
                <span style={{
                  marginLeft: '6px',
                  padding: '1px 7px',
                  background: 'var(--info-bg)',
                  borderRadius: '10px',
                  fontSize: '11px',
                  color: 'var(--info)',
                }}>
                  {category.code}
                </span>
                {category.report_group && category.report_group !== category.display_name_en && (
                  <span style={{ color: 'var(--text-muted)', fontSize: '12px', marginLeft: '4px' }}>
                    {category.report_group}
                  </span>
                )}
              </div>

              {/* Tasks table */}
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      {canEdit && <th style={{ ...thStyle, width: '30px' }} />}
                      <th style={{ ...thStyle, minWidth: '160px' }}>{t('taskDictTaskName')}</th>
                      <th style={{ ...thStyle, minWidth: '160px' }}>{t('taskDictCountingRule')}</th>
                      <th style={{ ...thStyle, minWidth: '120px' }}>{t('taskDictReportAlias')}</th>
                      <th style={{ ...thStyle, width: '90px', textAlign: 'center' }}>{t('taskDictIncludeInReport')}</th>
                      <th style={{ ...thStyle, width: '100px' }}>{t('taskDictRegionScope')}</th>
                      {canEdit && <th style={{ ...thStyle, width: '180px' }} />}
                    </tr>
                  </thead>
                  <tbody>
                    {category.tasks.length === 0 && !addForms[category.code] && (
                      <tr>
                        <td
                          colSpan={canEdit ? 7 : 5}
                          style={{ padding: '16px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '13px' }}
                        >
                          —
                        </td>
                      </tr>
                    )}
                    {category.tasks.map((task) => renderTaskRow(task, category.code))}
                    {canEdit && renderAddForm(category.code)}
                  </tbody>
                </table>
              </div>
            </div>
          ))}

          {categories.length === 0 && (
            <p style={{ color: 'var(--text-muted)', textAlign: 'center' }}>—</p>
          )}
        </div>
      )}

      {/* Noncompliant Records Section */}
      <div
        id="noncompliant-section"
        ref={noncompliantSectionRef}
        style={{ marginTop: '40px' }}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '16px',
          paddingBottom: '10px',
          borderBottom: '2px solid var(--border)',
        }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700 }}>{t('navNoncompliant')}</h3>
          <span style={{
            padding: '2px 8px',
            background: 'var(--warning-bg)',
            border: '1px solid var(--warning)',
            borderRadius: '4px',
            fontSize: '11px',
            color: 'var(--warning)',
          }}>
            {t('taskDictReadOnly') ?? 'Read-only'}
          </span>
        </div>

        {/* Noncompliant info banner */}
        <div style={{
          background: 'var(--warning-bg)',
          border: '1px solid var(--warning)',
          borderRadius: 6,
          padding: '10px 14px',
          marginBottom: 16,
          fontSize: 13,
          color: 'var(--warning)',
        }}>
          {t('noncompliantInfoBanner')}
        </div>

        {/* Noncompliant filters */}
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16, alignItems: 'flex-end' }}>
          <TimeRange value={ncTimeRange} onChange={(tr) => { setNcPage(1); setNcTimeRange(tr) }} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>{t('noncompliantRegionFilter')}</label>
            <select
              value={ncRegion}
              onChange={(e) => { setNcPage(1); setNcRegion(e.target.value) }}
              style={{
                padding: '6px 10px',
                border: '1px solid var(--border)',
                borderRadius: 4,
                fontSize: 14,
                background: 'var(--bg-surface)',
                color: 'var(--text-primary)',
                cursor: 'pointer',
              }}
            >
              <option value="">{t('noncompliantRegionAll')}</option>
              <option value="CN">CN</option>
              <option value="US">US</option>
            </select>
          </div>
        </div>

        {ncLoading && <div style={{ color: 'var(--text-muted)', padding: '16px 0' }}>{t('loading')}</div>}
        {ncError && <div style={{ color: 'var(--danger)', padding: '8px 0' }}>{ncError}</div>}

        {ncData && !ncLoading && (
          <div style={{ marginBottom: 10, fontSize: 13, color: 'var(--text-secondary)' }}>
            {t('noncompliantTotal')}: <strong>{ncData.page.total}</strong>
          </div>
        )}

        {!ncLoading && ncData && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: 13,
              background: 'var(--bg-surface)',
              border: '1px solid var(--border)',
              borderRadius: 6,
            }}>
              <thead>
                <tr style={{ background: 'var(--bg-page)', color: 'var(--text-secondary)', fontWeight: 600 }}>
                  {[
                    t('noncompliantColDate'),
                    t('noncompliantColMember'),
                    t('noncompliantColRegion'),
                    t('noncompliantColGroup'),
                    t('noncompliantColCategory'),
                    t('noncompliantColTask'),
                    t('noncompliantColVolume'),
                    t('noncompliantColHours'),
                    t('noncompliantColNote'),
                    t('noncompliantColStatus'),
                  ].map((header) => (
                    <th key={header} style={{
                      padding: '10px 12px',
                      textAlign: 'left',
                      borderBottom: '1px solid var(--border)',
                      whiteSpace: 'nowrap',
                    }}>
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ncData.data.length === 0 ? (
                  <tr>
                    <td colSpan={10} style={{ padding: '32px 12px', textAlign: 'center', color: 'var(--text-muted)' }}>
                      {t('noncompliantNoRecords')}
                    </td>
                  </tr>
                ) : (
                  ncData.data.map((rec) => (
                    <tr
                      key={rec.id}
                      style={{ borderBottom: '1px solid var(--border)' }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLTableRowElement).style.background = 'var(--bg-elevated)' }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLTableRowElement).style.background = '' }}
                    >
                      <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>{rec.work_date}</td>
                      <td style={{ padding: '8px 12px' }}>{rec.member_display_name}</td>
                      <td style={{ padding: '8px 12px' }}>{rec.region_code}</td>
                      <td style={{ padding: '8px 12px' }}>{rec.group_name}</td>
                      <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>{rec.category_display_name_en}</td>
                      <td style={{ padding: '8px 12px' }}>{rec.task_name}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'right' }}>{rec.volume}</td>
                      <td style={{ padding: '8px 12px', textAlign: 'right' }}>{rec.hours}h</td>
                      <td style={{
                        padding: '8px 12px',
                        color: 'var(--text-muted)',
                        maxWidth: 200,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }} title={rec.note}>
                        {rec.note || '—'}
                      </td>
                      <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                        <span style={{
                          background: 'var(--danger-bg)',
                          border: '1px solid var(--danger)',
                          borderRadius: 4,
                          padding: '2px 8px',
                          color: 'var(--danger)',
                          fontSize: 12,
                          fontWeight: 500,
                        }}>
                          {t('noncompliantStatusLabel')}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {ncData && Math.ceil(ncData.page.total / ncData.page.page_size) > 1 && (() => {
          const totalPages = Math.ceil(ncData.page.total / ncData.page.page_size)
          return (
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 16, flexWrap: 'wrap' }}>
              <button
                disabled={ncPage <= 1}
                onClick={() => setNcPage((p) => Math.max(1, p - 1))}
                style={{
                  padding: '4px 12px',
                  border: '1px solid var(--border)',
                  borderRadius: 4,
                  background: ncPage <= 1 ? 'var(--bg-page)' : 'var(--bg-surface)',
                  cursor: ncPage <= 1 ? 'not-allowed' : 'pointer',
                  fontSize: 13,
                }}
              >‹</button>
              <span style={{ lineHeight: '30px', fontSize: 13, color: 'var(--text-secondary)' }}>
                {ncPage} / {totalPages}
              </span>
              <button
                disabled={ncPage >= totalPages}
                onClick={() => setNcPage((p) => Math.min(totalPages, p + 1))}
                style={{
                  padding: '4px 12px',
                  border: '1px solid var(--border)',
                  borderRadius: 4,
                  background: ncPage >= totalPages ? 'var(--bg-page)' : 'var(--bg-surface)',
                  cursor: ncPage >= totalPages ? 'not-allowed' : 'pointer',
                  fontSize: 13,
                }}
              >›</button>
            </div>
          )
        })()}
      </div>

      {/* Hard delete modal */}
      {hardDelete && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'var(--overlay)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setHardDelete(null)
          }}
        >
          <div style={{
            background: 'var(--bg-surface)',
            color: 'var(--text-primary)',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '440px',
            width: '100%',
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
          }}>
            <h3 style={{ margin: '0 0 12px', fontSize: '16px' }}>{t('taskDictHardDeleteTitle')}</h3>
            <p style={{ margin: '0 0 14px', fontSize: '13px', color: 'var(--text-primary)', lineHeight: '1.6' }}>
              {hardDelete.refCount === null
                ? t('loading')
                : t('taskDictHardDeleteBody').replace('{N}', String(hardDelete.refCount))}
            </p>
            <p style={{ margin: '0 0 6px', fontSize: '13px', color: 'var(--text-secondary)' }}>
              {t('taskDictHardDeleteInput')}:
            </p>
            <input
              style={{ ...inputStyle, marginBottom: '14px' }}
              value={hardDelete.inputValue}
              onChange={(e) =>
                setHardDelete((prev) => prev ? { ...prev, inputValue: e.target.value } : null)
              }
              placeholder={hardDelete.task.name}
              autoFocus
            />
            {hardDelete.error && (
              <div style={{ color: 'var(--danger)', fontSize: '12px', marginBottom: '10px' }}>{hardDelete.error}</div>
            )}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                style={btnBase}
                disabled={hardDelete.submitting}
                onClick={() => setHardDelete(null)}
              >
                {t('taskDictCancel')}
              </button>
              <button
                style={{
                  ...btnDanger,
                  opacity: hardDelete.inputValue === hardDelete.task.name ? 1 : 0.4,
                  cursor: hardDelete.inputValue === hardDelete.task.name ? 'pointer' : 'not-allowed',
                }}
                disabled={hardDelete.inputValue !== hardDelete.task.name || hardDelete.submitting}
                onClick={confirmHardDelete}
              >
                {t('taskDictHardDeleteConfirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
